import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { User, Lock, CheckCircle2 } from "lucide-react";

export default function Profile() {
  const { user, updateUser } = useAuth();

  const [name, setName] = useState(user?.name || "");
  const [nameSuccess, setNameSuccess] = useState(false);
  const [nameError, setNameError] = useState("");
  const [nameSaving, setNameSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  async function saveName(e) {
    e.preventDefault();
    setNameError("");
    setNameSuccess(false);
    if (!name.trim()) { setNameError("Name cannot be empty."); return; }
    setNameSaving(true);
    try {
      const { data } = await api.patch("/auth/me", { name: name.trim() });
      updateUser({ name: data.name });
      setName(data.name);
      setNameSuccess(true);
    } catch (err) {
      setNameError(formatApiError(err));
    } finally {
      setNameSaving(false);
    }
  }

  async function savePassword(e) {
    e.preventDefault();
    setPwError("");
    setPwSuccess(false);
    if (!currentPassword) { setPwError("Enter your current password."); return; }
    if (newPassword.length < 8) { setPwError("New password must be at least 8 characters."); return; }
    if (newPassword !== confirmPassword) { setPwError("Passwords do not match."); return; }
    setPwSaving(true);
    try {
      await api.patch("/auth/me", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPwSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPwError(formatApiError(err));
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <div className="space-y-6 max-w-xl" data-testid="profile-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#8B918B" }}>
          Account
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>
          My Profile
        </h1>
      </div>

      <div className="rounded-2xl border p-6 space-y-4" style={{ borderColor: "var(--fb-border)", background: "#fff" }}>
        <div className="flex items-center gap-2 mb-1">
          <User className="w-4 h-4" style={{ color: "var(--fb-primary)" }} />
          <span className="font-semibold text-sm">Display Name</span>
        </div>
        <div className="text-xs mb-3" style={{ color: "var(--fb-text-muted)" }}>
          Signed in as <span className="font-medium">{user?.email}</span>
        </div>
        {user?.role && (
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Role</span>
            <span
              data-testid="profile-role-badge"
              className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize"
              style={{
                background: user.role === "admin" ? "#EFF6FF" : user.role === "viewer" ? "#F5F3FF" : "#F0FDF4",
                color: user.role === "admin" ? "#1D4ED8" : user.role === "viewer" ? "#6D28D9" : "#15803D",
                border: `1px solid ${user.role === "admin" ? "#BFDBFE" : user.role === "viewer" ? "#DDD6FE" : "#BBF7D0"}`,
              }}
            >
              {user.role}
            </span>
          </div>
        )}
        <form onSubmit={saveName} className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: "var(--fb-text-muted)" }}>
              Name
            </label>
            <input
              data-testid="profile-name-input"
              className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[var(--fb-primary)]"
              style={{ borderColor: "var(--fb-border)" }}
              value={name}
              onChange={e => { setName(e.target.value); setNameSuccess(false); setNameError(""); }}
              placeholder="Your display name"
              autoComplete="name"
            />
          </div>
          {nameError && (
            <p className="text-xs font-medium" style={{ color: "#991B1B" }} data-testid="profile-name-error">
              {nameError}
            </p>
          )}
          {nameSuccess && (
            <p className="flex items-center gap-1 text-xs font-medium" style={{ color: "#065F46" }} data-testid="profile-name-success">
              <CheckCircle2 className="w-3.5 h-3.5" /> Name updated.
            </p>
          )}
          <button
            data-testid="profile-save-name-btn"
            type="submit"
            disabled={nameSaving}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-60"
            style={{ background: "var(--fb-primary)" }}
          >
            {nameSaving ? "Saving…" : "Save Name"}
          </button>
        </form>
      </div>

      <div className="rounded-2xl border p-6 space-y-4" style={{ borderColor: "var(--fb-border)", background: "#fff" }}>
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-4 h-4" style={{ color: "var(--fb-primary)" }} />
          <span className="font-semibold text-sm">Change Password</span>
        </div>
        <form onSubmit={savePassword} className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: "var(--fb-text-muted)" }}>
              Current Password
            </label>
            <input
              data-testid="profile-current-password-input"
              type="password"
              className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[var(--fb-primary)]"
              style={{ borderColor: "var(--fb-border)" }}
              value={currentPassword}
              onChange={e => { setCurrentPassword(e.target.value); setPwSuccess(false); setPwError(""); }}
              placeholder="Enter current password"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: "var(--fb-text-muted)" }}>
              New Password
            </label>
            <input
              data-testid="profile-new-password-input"
              type="password"
              className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[var(--fb-primary)]"
              style={{ borderColor: "var(--fb-border)" }}
              value={newPassword}
              onChange={e => { setNewPassword(e.target.value); setPwSuccess(false); setPwError(""); }}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: "var(--fb-text-muted)" }}>
              Confirm New Password
            </label>
            <input
              data-testid="profile-confirm-password-input"
              type="password"
              className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[var(--fb-primary)]"
              style={{ borderColor: "var(--fb-border)" }}
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setPwSuccess(false); setPwError(""); }}
              placeholder="Repeat new password"
              autoComplete="new-password"
            />
          </div>
          {pwError && (
            <p className="text-xs font-medium" style={{ color: "#991B1B" }} data-testid="profile-pw-error">
              {pwError}
            </p>
          )}
          {pwSuccess && (
            <p className="flex items-center gap-1 text-xs font-medium" style={{ color: "#065F46" }} data-testid="profile-pw-success">
              <CheckCircle2 className="w-3.5 h-3.5" /> Password updated successfully.
            </p>
          )}
          <button
            data-testid="profile-save-password-btn"
            type="submit"
            disabled={pwSaving}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-60"
            style={{ background: "var(--fb-primary)" }}
          >
            {pwSaving ? "Saving…" : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
